export const FREE_PREVIEW_LIMIT = 1;

export const FREE_PREVIEW_OFFER_VARIANT = "basic_with_mini_v1";

export type FreePreviewGenerationLike = {
  isFreePreview?: boolean | null;
  status: string;
};

export function countConsumedFreePreviews(
  generations: readonly FreePreviewGenerationLike[]
): number {
  return generations.filter(
    (g) => g.isFreePreview === true && g.status === "completed"
  ).length;
}

export function hasInFlightFreePreview(
  generations: readonly FreePreviewGenerationLike[]
): boolean {
  return generations.some(
    (g) =>
      g.isFreePreview === true &&
      (g.status === "pending" || g.status === "processing")
  );
}

export function getRemainingFreePreviews(freePreviewsUsed: number): number {
  return Math.max(0, FREE_PREVIEW_LIMIT - freePreviewsUsed);
}

export function getCompletedFreePreviewNumber(
  remainingFreePreviews: number
): 1 | 2 {
  const completed = Math.max(1, FREE_PREVIEW_LIMIT - remainingFreePreviews);
  return completed >= 2 ? 2 : 1;
}

export type FreePreviewEligibilityInput = {
  isCompanyMember: boolean;
  hasPurchasedAnyPack: boolean;
  creditBalance: number;
  freePreviewsUsed: number;
  hasInFlightFreePreview: boolean;
};

export function canUseFreePreviewForUser(
  input: FreePreviewEligibilityInput
): boolean {
  return (
    !input.isCompanyMember &&
    !input.hasPurchasedAnyPack &&
    input.creditBalance < 1 &&
    getRemainingFreePreviews(input.freePreviewsUsed) > 0 &&
    !input.hasInFlightFreePreview
  );
}

/** Credits page: unauthenticated visitors still see the free tier CTA. */
export function canUseFreePreviewOnCreditsPage(
  isAuthenticated: boolean,
  input: FreePreviewEligibilityInput
): boolean {
  if (!isAuthenticated) {
    return true;
  }
  return canUseFreePreviewForUser(input);
}

export function formatFreePreviewLimitError(): string {
  return FREE_PREVIEW_LIMIT === 1
    ? "You have used your free preview"
    : `You have used all ${FREE_PREVIEW_LIMIT} free previews`;
}

export function formatFreePreviewCountLabel(count: number = FREE_PREVIEW_LIMIT): string {
  return `${count} watermarked preview${count === 1 ? "" : "s"}`;
}

export function formatFreePreviewTrustLine(): string {
  return `${FREE_PREVIEW_LIMIT} free watermarked preview${FREE_PREVIEW_LIMIT > 1 ? "s" : ""}.`;
}

export type CreditsFreePreviewFields = {
  isCompanyMember?: boolean;
  hasPurchasedAnyPack?: boolean;
  balance?: number;
  freePreviewsUsed?: number;
  hasInFlightFreePreview?: boolean;
};

export function freePreviewEligibilityFromCredits(
  credits: CreditsFreePreviewFields | undefined
): FreePreviewEligibilityInput {
  return {
    isCompanyMember: credits?.isCompanyMember ?? false,
    hasPurchasedAnyPack: credits?.hasPurchasedAnyPack ?? false,
    creditBalance: credits?.balance ?? 0,
    freePreviewsUsed: credits?.freePreviewsUsed ?? 0,
    hasInFlightFreePreview: credits?.hasInFlightFreePreview ?? false,
  };
}
